import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, UserPlus, Copy, Mail, Trash2, MapPin, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

function StatusBadge({ active }) {
  return active ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#E6F6ED] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#166534]">
      <CheckCircle2 className="h-3 w-3" /> Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#fff5e6] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#B45309]">
      <AlertTriangle className="h-3 w-3" /> Invite pending
    </span>
  );
}

const BLANK_INVITE = {
  email: "",
  name: "",
  phone: "",
  firm_name: "",
  bar_number: "",
  states: ["NY"],
  assigned_towns: "",
};

export default function AttorneysPanel({ open, onOpenChange }) {
  const [attorneys, setAttorneys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState(BLANK_INVITE);
  const [posting, setPosting] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editTowns, setEditTowns] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/attorneys");
      setAttorneys(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const toggleState = (s) => {
    setForm((f) => ({
      ...f,
      states: f.states.includes(s) ? f.states.filter((x) => x !== s) : [...f.states, s],
    }));
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name) {
      toast.error("Email and name are required");
      return;
    }
    setPosting(true);
    try {
      const towns = form.assigned_towns
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      const payload = {
        email: form.email.trim(),
        name: form.name.trim(),
        phone: form.phone.trim() || undefined,
        firm_name: form.firm_name.trim() || undefined,
        bar_number: form.bar_number.trim() || undefined,
        states: form.states,
        assigned_towns: towns,
      };
      const { data } = await api.post("/admin/attorneys/invite", payload);
      setLastInvite({ name: payload.name, email: payload.email, url: data.invite_url });
      setForm(BLANK_INVITE);
      setShowInvite(false);
      toast.success(`Invite sent to ${payload.email}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setPosting(false);
    }
  };

  const saveTowns = async (att) => {
    try {
      const towns = editTowns.split(",").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.patch(`/admin/attorneys/${att.id}`, { assigned_towns: towns });
      setAttorneys((prev) => prev.map((a) => (a.id === att.id ? { ...a, ...data } : a)));
      setEditingId(null);
      toast.success("Territories updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (att) => {
    if (!confirm(`Deactivate ${att.name}? Their assigned leads will be unassigned.`)) return;
    try {
      await api.delete(`/admin/attorneys/${att.id}`);
      toast.success("Attorney deactivated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="attorneys-panel">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black text-[#11322A]">
              Manage Attorneys
            </DialogTitle>
          </DialogHeader>

          {lastInvite && (
            <div className="mt-2 rounded-xl border border-[#bce5cc] bg-[#E6F6ED] p-4 text-sm" data-testid="invite-success">
              <div className="font-bold text-[#166534]">Invite sent to {lastInvite.email}</div>
              <p className="mt-1 text-xs text-[#166534]">
                We emailed them a 7-day acceptance link. Copy the URL below if email delivery is delayed:
              </p>
              <div className="mt-2 flex items-center gap-2 rounded-md bg-white p-2 text-[11px] text-[#0B1317]">
                <code className="flex-1 break-all">{lastInvite.url}</code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(lastInvite.url);
                    toast.success("Link copied");
                  }}
                  className="inline-flex shrink-0 items-center gap-1 rounded border border-[#E0E4E2] bg-[#FDFBF7] px-2 py-1 text-xs font-bold hover:border-[#11322A]"
                  data-testid="invite-copy"
                >
                  <Copy className="h-3 w-3" /> Copy
                </button>
              </div>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-wider text-[#5C6B68]">
              {attorneys.length} attorney{attorneys.length === 1 ? "" : "s"}
            </div>
            <button
              onClick={() => setShowInvite(true)}
              className="btn-primary text-sm"
              data-testid="invite-attorney-btn"
            >
              <UserPlus className="h-4 w-4" /> Invite attorney
            </button>
          </div>

          {loading ? (
            <div className="mt-6 grid place-items-center p-12">
              <Loader2 className="h-6 w-6 animate-spin text-[#11322A]" />
            </div>
          ) : attorneys.length === 0 ? (
            <div className="mt-6 card-soft p-12 text-center" data-testid="attorneys-empty">
              <UserPlus className="mx-auto h-8 w-8 text-[#5C6B68]" />
              <h3 className="mt-3 font-display text-lg font-bold text-[#11322A]">No attorneys yet</h3>
              <p className="mt-1 text-sm text-[#5C6B68]">Invite your first attorney to start assigning leads.</p>
            </div>
          ) : (
            <ul className="mt-6 divide-y divide-[#E0E4E2] rounded-xl border border-[#E0E4E2] bg-white" data-testid="attorneys-list">
              {attorneys.map((a) => (
                <li key={a.id} className="grid gap-3 p-4 sm:grid-cols-12 sm:items-start">
                  <div className="sm:col-span-4">
                    <div className="flex items-center gap-2">
                      <div className="font-display text-base font-bold text-[#11322A]">{a.name}</div>
                      <StatusBadge active={a.active} />
                    </div>
                    {a.firm_name && <div className="text-xs text-[#5C6B68]">{a.firm_name}</div>}
                    <div className="mt-1 flex items-center gap-1 text-xs text-[#0B1317]">
                      <Mail className="h-3 w-3" />
                      {a.email}
                    </div>
                    {a.phone && <div className="text-xs text-[#5C6B68]">{a.phone}</div>}
                  </div>
                  <div className="sm:col-span-2 text-xs">
                    <div className="label-cap text-[9px]">Caseload</div>
                    <div className="mt-1 font-display text-lg font-black text-[#11322A]">
                      {a.case_counts?.open ?? 0}
                    </div>
                    <div className="text-[10px] text-[#5C6B68]">open · {a.case_counts?.closed ?? 0} closed</div>
                  </div>
                  <div className="sm:col-span-4">
                    <div className="label-cap text-[9px]">
                      <MapPin className="inline h-2.5 w-2.5" /> Towns covered
                    </div>
                    {editingId === a.id ? (
                      <div className="mt-1 flex gap-2">
                        <input
                          className="input-field flex-1 text-xs"
                          value={editTowns}
                          onChange={(e) => setEditTowns(e.target.value)}
                          placeholder="Brooklyn, Queens, Newark"
                          data-testid={`att-towns-input-${a.id}`}
                        />
                        <button
                          onClick={() => saveTowns(a)}
                          className="btn-primary !py-1.5 text-xs"
                          data-testid={`att-towns-save-${a.id}`}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="rounded-md border border-[#E0E4E2] bg-white px-2 py-1 text-xs"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div
                        className="mt-1 cursor-pointer text-xs text-[#0B1317] hover:text-[#E85D04]"
                        onClick={() => {
                          setEditingId(a.id);
                          setEditTowns((a.assigned_towns || []).join(", "));
                        }}
                        data-testid={`att-towns-${a.id}`}
                      >
                        {(a.assigned_towns || []).length
                          ? (a.assigned_towns || []).join(" · ")
                          : <span className="italic text-[#5C6B68]">Click to add towns</span>}
                      </div>
                    )}
                    <div className="mt-2 flex gap-1.5">
                      {(a.states || []).map((s) => (
                        <span key={s} className="rounded-full bg-[#FDFBF7] px-2 py-0.5 text-[10px] font-bold text-[#11322A]">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="sm:col-span-2 flex items-center justify-end">
                    <button
                      onClick={() => remove(a)}
                      className="inline-flex items-center gap-1 rounded-md border border-[#F5B6B0] bg-white px-2 py-1 text-xs font-bold text-[#B91C1C] hover:bg-[#FDE8E6]"
                      data-testid={`att-remove-${a.id}`}
                    >
                      <Trash2 className="h-3 w-3" /> Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="max-w-lg" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black text-[#11322A]">Invite attorney</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitInvite} className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="label-cap">Full name</label>
                <input
                  className="input-field mt-2"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="invite-name"
                />
              </div>
              <div>
                <label className="label-cap">Email</label>
                <input
                  type="email"
                  className="input-field mt-2"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  data-testid="invite-email"
                />
              </div>
              <div>
                <label className="label-cap">Phone</label>
                <input
                  className="input-field mt-2"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="invite-phone"
                />
              </div>
              <div>
                <label className="label-cap">Firm name</label>
                <input
                  className="input-field mt-2"
                  value={form.firm_name}
                  onChange={(e) => setForm({ ...form, firm_name: e.target.value })}
                  data-testid="invite-firm"
                />
              </div>
              <div>
                <label className="label-cap">Bar number</label>
                <input
                  className="input-field mt-2"
                  value={form.bar_number}
                  onChange={(e) => setForm({ ...form, bar_number: e.target.value })}
                  data-testid="invite-bar"
                />
              </div>
              <div>
                <label className="label-cap">Licensed states</label>
                <div className="mt-2 flex gap-2">
                  {["NY", "NJ"].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleState(s)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-bold ${
                        form.states.includes(s)
                          ? "border-[#11322A] bg-[#11322A] text-white"
                          : "border-[#E0E4E2] bg-white text-[#5C6B68] hover:border-[#11322A]"
                      }`}
                      data-testid={`invite-state-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <label className="label-cap">Towns covered (comma-separated, for auto-assign)</label>
              <input
                className="input-field mt-2"
                value={form.assigned_towns}
                onChange={(e) => setForm({ ...form, assigned_towns: e.target.value })}
                placeholder="Brooklyn, Queens, Newark, Jersey City"
                data-testid="invite-towns"
              />
              <p className="mt-1 text-[11px] text-[#5C6B68]">
                Leads with a matching city auto-route to this attorney (lightest-loaded wins ties).
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowInvite(false)}
                className="rounded-lg border border-[#E0E4E2] bg-white px-4 py-2 text-sm font-bold text-[#0B1317] hover:bg-[#FDFBF7]"
                data-testid="invite-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={posting}
                className="btn-primary disabled:opacity-50"
                data-testid="invite-submit"
              >
                {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Send invite
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
